import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    MarkerType,
    Position,
    Handle
} from 'reactflow';
import 'reactflow/dist/style.css';
import JsonView from '@uiw/react-json-view';
import { lightTheme } from '@uiw/react-json-view/light';
import { darkTheme } from '@uiw/react-json-view/dark';
import { useThemeStore } from '../stores/theme';
import dagre from '@dagrejs/dagre';
import {
    Check, 
    Loader2, 
    AlertCircle, 
    Clock, 
    RefreshCw, 
    Power, 
    PowerOff, 
    Wrench, 
    Download, 
    Search, 
    Filter,
    Boxes,
    Radio,
    Terminal,
    Sparkles,
    ShieldAlert,
    Code,
    X,
    LayoutGrid,
    ArrowDownCircle,
    ArrowRightCircle,
    Bot,
    GitFork
} from 'lucide-react';
import { api } from '../services/api';
import { workflowApi } from '../services/workflowApi';

const WorkflowNode = ({data}) => {
    const status = data.status || 'pending';
    const isTool = data.isTool;
    const isAgent = data.isAgent;
    const parentJob = data.parentJob;
    const subToolCount = data.subToolCount || 0;
    const isSelected = data.isSelected;

    const getStatusStyle = () => {
        if (status === 'success') {
            return {
                border: isAgent 
                    ? 'border-indigo-500 dark:border-indigo-400 shadow-indigo-500/30' 
                    : isTool 
                    ? 'border-purple-400 dark:border-purple-500/70 shadow-purple-500/20' 
                    : 'border-emerald-500 dark:border-emerald-500/80 shadow-emerald-500/20',
                glow: isAgent 
                    ? 'from-indigo-500/30 via-transparent to-transparent' 
                    : isTool 
                    ? 'from-purple-500/25 via-transparent to-transparent' 
                    : 'from-emerald-500/20 via-transparent to-transparent',
                badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30',
                dot: 'bg-emerald-500 dark:bg-emerald-400 shadow-emerald-400/50',
                accentColor: 'text-emerald-600 dark:text-emerald-400'
            };
        }
        if (status === 'running') {
            return {
                border: 'border-amber-500 dark:border-amber-400 shadow-amber-400/30',
                glow: 'from-amber-500/20 via-transparent to-transparent',
                badgeBg: 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/40',
                dot: 'bg-amber-500 dark:bg-amber-400 shadow-amber-400/50',
                accentColor: 'text-amber-600 dark:text-amber-400'
            };
        }
        if (status === 'fail') {
            return {
                border: 'border-red-500 shadow-red-500/30',
                glow: 'from-red-500/20 via-transparent to-transparent',
                badgeBg: 'bg-red-50 text-red-700 border-red-300 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/40',
                dot: 'bg-red-500 dark:bg-red-400 shadow-red-400/50',
                accentColor: 'text-red-600 dark:text-red-400'
            };
        }
        return {
            border: isAgent 
                ? 'border-indigo-400 dark:border-indigo-600 shadow-indigo-500/10' 
                : isTool 
                ? 'border-purple-300 dark:border-purple-800 shadow-purple-500/10' 
                : 'border-slate-300 dark:border-slate-700/80 shadow-slate-200 dark:shadow-slate-900/40',
            glow: isAgent 
                ? 'from-indigo-500/20 via-transparent to-transparent' 
                : isTool 
                ? 'from-purple-500/15 via-transparent to-transparent' 
                : 'from-indigo-500/10 via-transparent to-transparent',
            badgeBg: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
            dot: 'bg-slate-400 dark:bg-slate-600',
            accentColor: 'text-slate-500 dark:text-slate-400'
        };
    };

    const style = getStatusStyle();

    return (
        <div 
            className={`group relative min-w-[210px] rounded-xl backdrop-blur-md transition-all duration-300 cursor-pointer select-none
                ${isSelected 
                    ? 'ring-2 ring-indigo-500 shadow-lg shadow-indigo-500/30 scale-105' 
                    : isAgent 
                    ? 'ring-1 ring-indigo-400/50 dark:ring-indigo-500/40 hover:scale-[1.02] hover:shadow-xl' 
                    : 'hover:scale-[1.02] hover:shadow-lg'
                }
                ${isTool ? 'bg-purple-50/40 dark:bg-slate-950/90' : isAgent ? 'bg-indigo-50/30 dark:bg-slate-950/90' : 'bg-white/95 dark:bg-slate-950/85'}
                border ${style.border} shadow-md overflow-hidden`}
            onClick={() => data.onClick?.(data.id)}
        >
            {/* Ambient Corner Glow */}
            <div className={`absolute -right-8 -top-8 w-20 h-20 rounded-full bg-gradient-to-br ${style.glow} blur-xl pointer-events-none`} />

            {/* Target handles on all 4 directions - discrete connection points */}
            <Handle 
                type="target" 
                position={Position.Left} 
                id="target-l"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />
            <Handle 
                type="target" 
                position={Position.Top} 
                id="target-t"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />
            <Handle 
                type="target" 
                position={Position.Right} 
                id="target-r"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />
            <Handle 
                type="target" 
                position={Position.Bottom} 
                id="target-b"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />

            {/* Source handles on all 4 directions - discrete connection points */}
            <Handle 
                type="source" 
                position={Position.Right} 
                id="source-r"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />
            <Handle 
                type="source" 
                position={Position.Bottom} 
                id="source-b"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />
            <Handle 
                type="source" 
                position={Position.Left} 
                id="source-l"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />
            <Handle 
                type="source" 
                position={Position.Top} 
                id="source-t"
                className="!w-2 !h-2 !bg-indigo-400 dark:!bg-indigo-500 !border !border-white dark:!border-slate-900 !opacity-0 group-hover:!opacity-100 transition-opacity"
            />

            <div className="p-3.5 relative z-10">
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <div className={`w-2 h-2 rounded-full ${style.dot} shadow-sm flex-shrink-0 ${status === 'running' ? 'animate-pulse' : ''}`} />
                        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 dark:text-slate-400 truncate font-semibold">
                            {isAgent ? 'AGENT CORE' : isTool ? `SUB-JOB OF ${parentJob || 'AGENT'}` : (data.jobName || 'JOB STEP')}
                        </span>
                    </div>

                    {isAgent ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-300 dark:bg-indigo-500/20 dark:text-indigo-300 dark:border-indigo-500/40 shadow-sm">
                            <Bot size={10} /> Orchestrator
                        </span>
                    ) : isTool ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30 shadow-sm">
                            <GitFork size={9} /> Sub-Job
                        </span>
                    ) : (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${style.badgeBg}`}>
                            {status}
                        </span>
                    )}
                </div>

                <div className="text-sm font-bold text-slate-900 dark:text-slate-100 font-mono tracking-tight leading-tight truncate" title={data.label}>
                    {data.label}
                </div>

                {isAgent && subToolCount > 0 && (
                    <div className="mt-1.5 flex items-center gap-1 text-[10px] font-mono text-purple-600 dark:text-purple-400 font-semibold">
                        <Wrench size={10} />
                        <span>{subToolCount} AI Sub-Job{subToolCount > 1 ? 's' : ''} Dispatched</span>
                    </div>
                )}

                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                    <div className="flex items-center gap-1">
                        <Clock size={11} className={style.accentColor} />
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                            {data.executionTime !== undefined 
                                ? `${(data.executionTime * 1000).toFixed(0)}ms` 
                                : '--'}
                        </span>
                    </div>
                    {data.hasOutput && (
                        <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold">Output ✓</span>
                    )}
                </div>
            </div>
        </div>
    );
};

const nodeTypes = {workflow: WorkflowNode};

export default function Workflows() {
    const isDark = useThemeStore((s) => s.dark);
    const [workflows, setWorkflows] = useState([]);
    const [realtime, setRealtime] = useState([]);
    const [selected, setSelected] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const [view, setView] = useState('history');
    const [liveMode, setLiveMode] = useState(true);
    const [streamStatus, setStreamStatus] = useState('idle'); // 'idle' | 'connecting' | 'connected' | 'error'
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [inspectorTab, setInspectorTab] = useState('overview');
    const [layoutMode, setLayoutMode] = useState('compact'); // 'compact' | 'vertical' | 'horizontal'
    
    const listStreamRef = useRef(null);
    const selectedStreamRef = useRef(null);
    const selectedKeyRef = useRef(null);
    const selectedNodeIdRef = useRef(null);
    const layoutModeRef = useRef(layoutMode);

    useEffect(() => {
        selectedKeyRef.current = selectedKey;
    }, [selectedKey]);

    useEffect(() => {
        selectedNodeIdRef.current = selectedNodeId;
    }, [selectedNodeId]);

    useEffect(() => {
        layoutModeRef.current = layoutMode;
    }, [layoutMode]);

    // Live workflow updates via Mercure: snapshot from /workflow/redis, then the private rodoud/workflow topic
    const startSSEStream = useCallback(({ onInit, onUpdate, onConnect, onError }) => {
        let es = null;
        let aborted = false;

        (async () => {
            try {
                const live = await workflowApi.getLive();
                if (aborted) return;

                if (onInit) {
                    const snapshot = await workflowApi.getRedisState();
                    if (aborted) return;
                    onInit(snapshot);
                }

                const url = new URL(live.hub_url || workflowApi.defaultHubUrl());
                url.searchParams.append('topic', live.topic);
                url.searchParams.append('authorization', live.token);

                es = new EventSource(url.toString());
                es.onopen = () => onConnect?.();
                es.onmessage = (e) => {
                    try {
                        onUpdate?.(JSON.parse(e.data), 'update');
                    } catch (err) {
                        console.warn('Failed to parse workflow update:', err);
                    }
                };
                es.onerror = (err) => {
                    // Network blips reconnect on their own; CLOSED means the hub refused us (e.g. token expired)
                    if (es.readyState === EventSource.CLOSED) {
                        es.close();
                        onError?.(err);
                    }
                };
            } catch (err) {
                if (!aborted) onError?.(err);
            }
        })();

        return {
            abort: () => {
                aborted = true;
                es?.close();
            }
        };
    }, []);

    const selectedNode = useMemo(() => {
        if (!selectedNodeId || !selected) return null;
        const exec = selected.executed_jobs || {};
        const steps = selected.steps || [];
        const job = exec[selectedNodeId] || {};
        const step = steps.find(s => s.name === selectedNodeId) || null;
        const results = selected.results || {};
        return {id: selectedNodeId, ...job, step, output: results[selectedNodeId]};
    }, [selectedNodeId, selected]);

    const fmtDate = (t) => {
        if (!t) return 'N/A';
        const d = typeof t === 'number' ? new Date(t * 1000) : new Date(t);
        return d.toLocaleString();
    };
    
    const fmtDur = (s) => {
        if (s === undefined || s === null) return 'N/A';
        return s * 1000 > 999 ? `${s.toFixed(2)}s` : `${(s * 1000).toFixed(2)}ms`;
    };

    const fetchHistory = async () => {
        setIsRefreshing(true);
        try {
            const r = await api.get('/api/v1/workflow/history');
            setWorkflows(Array.isArray(r) ? r : (r?.data || []));
        } catch (error) {
            console.error('Error fetching history:', error);
            try {
                const r = await api.get('/api/v1/admin/workflow/history');
                setWorkflows(Array.isArray(r) ? r : (r?.data || []));
            } catch (err2) {
                console.error('Fallback history failed:', err2);
            }
        } finally {
            setIsRefreshing(false);
        }
    };

    const fetchLive = async () => {
        setIsRefreshing(true);
        try {
            const r = await api.get('/api/v1/workflow/redis');
            setRealtime(Array.isArray(r) ? r : (r?.data || []));
        } catch (error) {
            console.error('Error fetching live data:', error);
            try {
                const r = await api.get('/api/v1/admin/workflow/redis');
                setRealtime(Array.isArray(r) ? r : (r?.data || []));
            } catch (err2) {
                console.error('Fallback live data failed:', err2);
            }
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleRefresh = () => {
        if (view === 'history') fetchHistory();
        else fetchLive();
    };

    const toggleLiveMode = () => {
        setLiveMode(!liveMode);
    };

    useEffect(() => {
        fetchHistory();
    }, []);


    const toGraph = useCallback((wf, activeNodeId = null, currentLayout = layoutMode) => {
        const steps = wf.steps || [];
        const exec = wf.executed_jobs || {};
        const results = wf.results || {};
        const conns = [];

        steps.forEach(s => {
            const list = s.connections || s.next || [];
            list.forEach(target => {
                conns.push({from: s.name, to: target});
            });
        });

        // Compute Layout Positions
        const pos = {};
        const NODE_WIDTH = 230;
        const NODE_HEIGHT = 90;

        if (currentLayout === 'vertical' || currentLayout === 'horizontal') {
            const g = new dagre.graphlib.Graph();
            g.setDefaultEdgeLabel(() => ({}));
            g.setGraph({
                rankdir: currentLayout === 'vertical' ? 'TB' : 'LR',
                nodesep: currentLayout === 'vertical' ? 35 : 45,
                ranksep: currentLayout === 'vertical' ? 65 : 80,
                marginx: 40,
                marginy: 40,
            });

            steps.forEach(s => {
                g.setNode(s.name, { width: NODE_WIDTH, height: NODE_HEIGHT });
            });

            conns.forEach(c => {
                g.setEdge(c.from, c.to);
            });

            dagre.layout(g);

            steps.forEach(s => {
                const node = g.node(s.name);
                if (node) {
                    pos[s.name] = {
                        x: Math.round(node.x - NODE_WIDTH / 2),
                        y: Math.round(node.y - NODE_HEIGHT / 2)
                    };
                }
            });
        } else {
            // Default 'compact' mode: Multi-column wrapping grid
            const deps = {};
            conns.forEach(c => {
                deps[c.to] = deps[c.to] || [];
                deps[c.to].push(c.from);
            });

            const levels = {};
            const visited = new Set();
            const calcLevel = (n) => {
                if (levels[n] !== undefined) return levels[n];
                if (visited.has(n)) return 1;
                visited.add(n);
                const d = deps[n] || [];
                const maxLevel = d.length ? Math.max(...d.map(calcLevel)) : 0;
                return levels[n] = maxLevel + 1;
            };
            steps.forEach(s => calcLevel(s.name));

            // Multi-column wrapping grid with dynamic row height & parallel branch expansion
            const levelGroups = {};
            Object.entries(levels).forEach(([name, level]) => {
                levelGroups[level] = levelGroups[level] || [];
                levelGroups[level].push(name);
            });

            const sortedLevels = Object.keys(levelGroups).sort((a, b) => Number(a) - Number(b));
            const totalLevels = sortedLevels.length;
            const maxCols = totalLevels <= 4 ? Math.max(totalLevels, 1) : (totalLevels <= 8 ? 3 : 4);

            const COL_SPACING = 310;
            const BASE_ROW_HEIGHT = 115;
            const PARALLEL_SPACING = 125;
            const ROW_GAP = 75;
            const BASE_X = 50;
            const BASE_Y = 50;

            let currentY = BASE_Y;

            for (let i = 0; i < totalLevels; i += maxCols) {
                const chunk = sortedLevels.slice(i, i + maxCols);
                let maxParallelInRow = 1;

                for (let c = 0; c < chunk.length; c++) {
                    const count = levelGroups[chunk[c]].length;
                    if (count > maxParallelInRow) maxParallelInRow = count;
                }

                const rowBaseY = currentY;
                for (let colIndex = 0; colIndex < chunk.length; colIndex++) {
                    const lvl = chunk[colIndex];
                    const nodesList = levelGroups[lvl];
                    const colX = BASE_X + colIndex * COL_SPACING;

                    for (let idx = 0; idx < nodesList.length; idx++) {
                        const name = nodesList[idx];
                        pos[name] = {
                            x: colX,
                            y: rowBaseY + idx * PARALLEL_SPACING
                        };
                    }
                }

                const rowHeight = BASE_ROW_HEIGHT + (maxParallelInRow - 1) * PARALLEL_SPACING + ROW_GAP;
                currentY += rowHeight;
            }
        }

        // Count sub-jobs dispatched by each parent job (e.g. runAgent)
        const subToolCounts = {};
        steps.forEach(step => {
            const isTool = Boolean(step.is_tool || step.name?.startsWith('tool_'));
            const parent = step.parent_job || (isTool ? 'runAgent' : null);
            if (parent) {
                subToolCounts[parent] = (subToolCounts[parent] || 0) + 1;
            }
        });

        const n = steps.map(step => {
            const job = exec[step.name] || {};
            const isTool = Boolean(step.is_tool || step.name?.startsWith('tool_'));
            const isAgent = step.name === 'runAgent' || step.job === 'runAgent';
            const cleanLabel = isTool && step.job ? step.job : step.name;
            const parentJob = step.parent_job || (isTool ? 'runAgent' : null);
            const subToolCount = isAgent ? (subToolCounts['runAgent'] || 0) : 0;

            return {
                id: step.name,
                type: 'workflow',
                position: pos[step.name] || {x: 0, y: 0},
                data: {
                    label: cleanLabel,
                    id: step.name,
                    jobName: step.job,
                    isTool: isTool,
                    isAgent: isAgent,
                    parentJob: parentJob,
                    subToolCount: subToolCount,
                    status: job.status || 'pending',
                    executionTime: job.performance?.execution_time,
                    hasOutput: Boolean(results[step.name]),
                    isSelected: activeNodeId === step.name,
                    onClick: (id) => {
                        setSelectedNodeId(id);
                        toGraph(wf, id, currentLayout);
                    }
                }
            };
        });

        const e = conns.map(c => {
            const st = exec[c.to]?.status;
            const isTargetTool = Boolean(c.to?.startsWith('tool_') || steps.find(s => s.name === c.to)?.is_tool);
            const isSourceAgent = c.from === 'runAgent';
            const isSourceTool = Boolean(c.from?.startsWith('tool_') || steps.find(s => s.name === c.from)?.is_tool);
            const isPipelineReturn = isSourceTool && !isTargetTool;

            let color = st === 'running' ? '#f59e0b' :
                st === 'success' 
                    ? (isSourceAgent && isTargetTool ? '#8b5cf6' : isPipelineReturn ? '#059669' : isTargetTool ? '#9333ea' : '#059669') 
                    : st === 'fail' ? '#ef4444' : (isDark ? '#475569' : '#94a3b8');

            const fromPos = pos[c.from] || { x: 0, y: 0 };
            const toPos = pos[c.to] || { x: 0, y: 0 };

            let sourceHandle = 'source-r';
            let targetHandle = 'target-l';

            if (currentLayout === 'vertical') {
                sourceHandle = 'source-b';
                targetHandle = 'target-t';
            } else if (currentLayout === 'horizontal') {
                sourceHandle = 'source-r';
                targetHandle = 'target-l';
            } else {
                // Compact / Grid mode smart handle routing
                const dy = toPos.y - fromPos.y;
                const dx = toPos.x - fromPos.x;

                if (dy > 40) {
                    // Target is below source
                    if (dx < -60) {
                        // Wraps to a new row on the left (e.g. buildMessages -> runAgent, or tool_merge -> storeConversation)
                        sourceHandle = 'source-b';
                        targetHandle = 'target-t';
                    } else if (Math.abs(dx) < 80) {
                        // Directly below
                        sourceHandle = 'source-b';
                        targetHandle = 'target-t';
                    } else {
                        // Downward branch to the right (e.g. tool: search_faq -> milvus)
                        sourceHandle = 'source-r';
                        targetHandle = 'target-l';
                    }
                } else if (dy < -40) {
                    // Target is above source (e.g. milvus -> tool_merge)
                    if (dx > 40) {
                        sourceHandle = 'source-r';
                        targetHandle = 'target-b';
                    } else {
                        sourceHandle = 'source-t';
                        targetHandle = 'target-b';
                    }
                } else {
                    // Same horizontal row
                    if (dx < -30) {
                        sourceHandle = 'source-l';
                        targetHandle = 'target-r';
                    } else {
                        sourceHandle = 'source-r';
                        targetHandle = 'target-l';
                    }
                }
            }

            return {
                id: `e-${c.from}-${c.to}`,
                source: c.from,
                target: c.to,
                sourceHandle,
                targetHandle,
                type: 'smoothstep',
                animated: st === 'running',
                label: isSourceAgent && isTargetTool ? '⚡ Sub-Job' : undefined,
                labelStyle: {
                    fill: '#a855f7',
                    fontWeight: 700,
                    fontSize: 9,
                    fontFamily: 'monospace'
                },
                labelBgStyle: {
                    fill: isDark ? '#020617' : '#ffffff',
                    fillOpacity: 0.95,
                    stroke: isDark ? '#475569' : '#cbd5e1',
                    strokeWidth: 1,
                    rx: 4,
                    ry: 4
                },
                labelBgPadding: [4, 2],
                style: {
                    stroke: color,
                    strokeWidth: (isSourceAgent && isTargetTool) ? 2.5 : 2,
                    strokeDasharray: (isSourceAgent && isTargetTool) ? '4,4' : st === 'pending' ? '5,5' : undefined
                },
                markerEnd: { type: MarkerType.ArrowClosed, color }
            };
        });

        setNodes(n);
        setEdges(e);
    }, [setNodes, setEdges, isDark, layoutMode]);

    const handleLayoutChange = (newMode) => {
        setLayoutMode(newMode);
        if (selected) {
            toGraph(selected, selectedNodeId, newMode);
        }
    };

    const openWorkflow = (wf) => {
        const content = wf.content || wf;
        const wfId = wf.id || content.workflow_id;
        setSelected(content);
        setSelectedKey(wfId);
        selectedKeyRef.current = wfId;
        setSelectedNodeId(null);
        selectedNodeIdRef.current = null;
        toGraph(content, null, layoutModeRef.current);

        if (selectedStreamRef.current) {
            selectedStreamRef.current.abort?.();
            selectedStreamRef.current = null;
        }

        // In live mode the list stream already updates the selected workflow; one hub connection is enough
        if (view === 'realtime' && !liveMode && wfId) {
            selectedStreamRef.current = startSSEStream({
                onUpdate: (updatedContent) => {
                    const data = updatedContent.content || updatedContent;
                    const updateId = data.workflow_id || updatedContent.id;
                    if (updateId === selectedKeyRef.current) {
                        setSelected(data);
                        toGraph(data, selectedNodeIdRef.current, layoutModeRef.current);
                    }
                }
            });
        }
    };

    // Real-time live workflows stream (Mercure)
    useEffect(() => {
        if (listStreamRef.current) {
            listStreamRef.current.abort?.();
            listStreamRef.current = null;
        }
        if (selectedStreamRef.current) {
            selectedStreamRef.current.abort?.();
            selectedStreamRef.current = null;
        }

        if (view !== 'realtime' || !liveMode) {
            setStreamStatus('idle');
            return;
        }

        setStreamStatus('connecting');
        let reconnectTimeout = null;

        const connect = () => {
            const controller = startSSEStream({
                onConnect: () => {
                    setStreamStatus('connected');
                },
                onInit: (initList) => {
                    const list = Array.isArray(initList) ? initList : (initList?.data || []);
                    setRealtime(list);
                    if (selectedKeyRef.current) {
                        const found = list.find((item) => {
                            const c = item.content || item;
                            return (c.workflow_id || item.id) === selectedKeyRef.current;
                        });
                        if (found) {
                            const content = found.content || found;
                            setSelected(content);
                            toGraph(content, selectedNodeIdRef.current, layoutModeRef.current);
                        }
                    }
                },
                onUpdate: (updatedWf) => {
                    const content = updatedWf.content || updatedWf;
                    const wfId = content.workflow_id || updatedWf.id;
                    if (!wfId) return;

                    // 1. Update realtime workflows list
                    setRealtime((prev) => {
                        const idx = prev.findIndex((item) => {
                            const c = item.content || item;
                            return (c.workflow_id || item.id) === wfId;
                        });
                        if (idx >= 0) {
                            const next = [...prev];
                            next[idx] = { key: `workflow:updates:${wfId}`, content };
                            return next;
                        }
                        return [{ key: `workflow:updates:${wfId}`, content }, ...prev];
                    });

                    // 2. If the updated workflow is the currently selected one, update the graph in real-time
                    if (selectedKeyRef.current === wfId) {
                        setSelected(content);
                        toGraph(content, selectedNodeIdRef.current, layoutModeRef.current);
                    }
                },
                onError: () => {
                    setStreamStatus('error');
                    reconnectTimeout = setTimeout(() => {
                        if (view === 'realtime' && liveMode) {
                            connect();
                        }
                    }, 3000);
                }
            });

            listStreamRef.current = controller;
        };

        connect();

        return () => {
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
            if (listStreamRef.current) {
                listStreamRef.current.abort?.();
                listStreamRef.current = null;
            }
            setStreamStatus('idle');
        };
    }, [view, liveMode, startSSEStream, toGraph]);

    useEffect(() => () => {
        if (listStreamRef.current) listStreamRef.current.abort?.();
        if (selectedStreamRef.current) selectedStreamRef.current.abort?.();
    }, []);

    const filteredList = useMemo(() => {
        let items = view === 'history' ? workflows : realtime;
        if (statusFilter !== 'all') {
            items = items.filter(wf => {
                const c = wf.content || wf;
                return c.status === statusFilter;
            });
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            items = items.filter(wf => {
                const c = wf.content || wf;
                return (c.name || '').toLowerCase().includes(q) || (c.workflow_id || '').toLowerCase().includes(q);
            });
        }
        return items;
    }, [view, workflows, realtime, statusFilter, searchQuery]);

    const exportWorkflow = () => {
        if (!selected) return;
        const blob = new Blob([JSON.stringify(selected, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workflow-${selected.workflow_id || 'export'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const StatusPill = ({status}) => {
        const isSuccess = status === 'success';
        const isRunning = status === 'running';
        const isFail = status === 'fail';

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase font-bold tracking-wider ${
                isSuccess 
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' 
                    : isRunning 
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' 
                    : isFail 
                    ? 'bg-red-500/10 text-red-400 border border-red-500/30' 
                    : 'bg-slate-800 text-slate-400 border border-slate-700'
            }`}>
                {isSuccess && <Check size={10} />}
                {isRunning && <Loader2 size={10} className="animate-spin" />}
                {isFail && <AlertCircle size={10} />}
                {status || 'pending'}
            </span>
        );
    };

    const isSelected = (wf, c) => (selectedKey && (selectedKey === (wf.id || c.workflow_id)));

    return (
        <div className="h-[calc(100vh-100px)] flex flex-col rounded-2xl overflow-hidden bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800/80 shadow-xl">
            {/* Top Control HUD */}
            <div className="h-14 px-5 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-shrink-0 z-20">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/20">
                        <Boxes size={18} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-bold text-slate-900 dark:text-white tracking-wide font-mono uppercase">
                                Neural Workflow Engine
                            </h2>
                            <span className="px-2 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-800">
                                v2.4 Runtime
                            </span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 hidden sm:block">
                            Distributed DAG execution graph, state propagation, and step latency tracing
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                    {/* View Switcher Tabs */}
                    <div className="flex p-1 bg-slate-100 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-mono">
                        <button
                            onClick={() => setView('history')}
                            className={`px-3 py-1 rounded-lg transition-all ${
                                view === 'history' 
                                    ? 'bg-indigo-600 text-white font-semibold shadow-sm shadow-indigo-600/30' 
                                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                            }`}
                        >
                            History ({workflows.length})
                        </button>
                        <button
                            onClick={() => setView('realtime')}
                            className={`px-3 py-1 rounded-lg flex items-center gap-1.5 transition-all ${
                                view === 'realtime' 
                                    ? 'bg-indigo-600 text-white font-semibold shadow-sm shadow-indigo-600/30' 
                                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                            }`}
                        >
                            <Radio size={12} className={liveMode ? 'text-emerald-500 dark:text-emerald-400 animate-pulse' : ''} />
                            Live Engine ({realtime.length})
                        </button>
                    </div>

                    {/* Live Mode Toggle & Stream Status */}
                    {view === 'realtime' && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={toggleLiveMode}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-mono font-semibold transition-all border ${
                                    liveMode
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-500/30 shadow-sm'
                                        : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-800 hover:border-slate-400'
                                }`}
                            >
                                {liveMode ? <Power size={12} className="text-emerald-600 dark:text-emerald-400" /> : <PowerOff size={12} />}
                                <span>{liveMode ? 'LIVE ON' : 'LIVE OFF'}</span>
                            </button>
                            {liveMode && (
                                <span className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[11px] font-mono font-semibold border ${
                                    streamStatus === 'connected'
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-500/30'
                                        : streamStatus === 'connecting'
                                        ? 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-500/30'
                                        : 'bg-red-50 text-red-700 border-red-300 dark:bg-red-950/50 dark:text-red-400 dark:border-red-500/30'
                                }`}>
                                    <span className={`w-2 h-2 rounded-full ${
                                        streamStatus === 'connected'
                                            ? 'bg-emerald-500 shadow-sm shadow-emerald-500 animate-pulse'
                                            : streamStatus === 'connecting'
                                            ? 'bg-amber-500 animate-spin'
                                            : 'bg-red-500'
                                    }`} />
                                    {streamStatus === 'connected'
                                        ? 'Redis Stream Active'
                                        : streamStatus === 'connecting'
                                        ? 'Connecting Stream...'
                                        : 'Stream Offline'}
                                </span>
                            )}
                        </div>
                    )}

                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-white hover:bg-slate-100 dark:bg-slate-800/80 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700/60 transition-all disabled:opacity-50"
                        title="Sync workflows"
                    >
                        <RefreshCw size={13} className={isRefreshing ? 'animate-spin text-indigo-500 dark:text-indigo-400' : ''} />
                        <span className="hidden sm:inline">Sync</span>
                    </button>

                    {selected && (
                        <button
                            onClick={exportWorkflow}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-white hover:bg-slate-100 dark:bg-slate-800/80 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700/60 transition-all"
                            title="Export Workflow JSON"
                        >
                            <Download size={13} />
                            <span className="hidden sm:inline">Export</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Main Workspace Split (List + Visualizer) */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Workflow Library Panel */}
                <div className="w-80 min-w-[300px] bg-slate-100/70 dark:bg-slate-950/90 border-r border-slate-200 dark:border-slate-800 flex flex-col flex-shrink-0">
                    {/* Search & Filter Bar */}
                    <div className="p-3 border-b border-slate-200 dark:border-slate-800/80 space-y-2 bg-white/60 dark:bg-slate-900/40">
                        <div className="relative">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                type="text"
                                placeholder="Search workflow or ID..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 text-xs font-mono bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter size={12} className="text-slate-400 dark:text-slate-500" />
                            <select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                className="flex-1 text-xs font-mono bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-lg py-1 px-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-slate-800 dark:text-slate-300"
                            >
                                <option value="all">All States</option>
                                <option value="success">Success</option>
                                <option value="running">Running</option>
                                <option value="fail">Failed</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                    </div>

                    {/* Filtered Workflow List */}
                    <div className="flex-1 overflow-y-auto p-2.5 space-y-2 scrollbar-thin">
                        {filteredList.map(wf => {
                            const c = wf.content || wf;
                            const active = isSelected(wf, c);
                            const stepCount = (c.steps || []).length;

                            return (
                                <button
                                    key={wf.id || c.workflow_id}
                                    onClick={() => openWorkflow(wf)}
                                    className={`w-full text-left rounded-xl p-3 transition-all duration-200 border relative overflow-hidden group ${
                                        active 
                                            ? 'bg-white dark:bg-indigo-950/40 border-indigo-500 shadow-md shadow-indigo-100 dark:shadow-indigo-950/50' 
                                            : 'bg-white/80 dark:bg-slate-900/40 hover:bg-white dark:hover:bg-slate-900/80 border-slate-200 dark:border-slate-800/80 hover:border-slate-300 dark:hover:border-slate-700'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="font-semibold text-xs text-slate-900 dark:text-slate-200 line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-white transition-colors" title={c.name}>
                                            {c.name || 'Autonomous Agent Pipeline'}
                                        </div>
                                        <StatusPill status={c.status} />
                                    </div>

                                    <div className="grid grid-cols-2 gap-y-1.5 text-[11px] font-mono text-slate-500 dark:text-slate-400">
                                        <div>
                                            <span className="text-slate-400 dark:text-slate-600 block text-[9px] uppercase">Execution ID</span>
                                            <span className="truncate block text-slate-700 dark:text-slate-300 font-bold">{c.workflow_id ? `${c.workflow_id.substring(0, 10)}...` : 'N/A'}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-slate-400 dark:text-slate-600 block text-[9px] uppercase">Latency</span>
                                            <span className="text-slate-700 dark:text-slate-300 font-bold">{fmtDur(c?.performance?.execution_time)}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-400 dark:text-slate-600 block text-[9px] uppercase">DAG Steps</span>
                                            <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{stepCount} nodes</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-slate-400 dark:text-slate-600 block text-[9px] uppercase">Started</span>
                                            <span className="text-slate-600 dark:text-slate-400">{c?.performance?.start_time ? new Date(c.performance.start_time * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '--'}</span>
                                        </div>
                                    </div>

                                    {active && (
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500" />
                                    )}
                                </button>
                            );
                        })}

                        {filteredList.length === 0 && (
                            <div className="p-8 text-center text-xs text-slate-400 dark:text-slate-500 font-mono">
                                No workflows matching filter criteria.
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Visualizer Canvas & Inspector */}
                <div className="flex-1 bg-slate-100/60 dark:bg-slate-950 relative overflow-hidden flex flex-col">
                    {!selected ? (
                        <div className="h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 space-y-3 bg-grid-pattern">
                            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-400 shadow-lg">
                                <Boxes size={32} />
                            </div>
                            <p className="text-sm font-mono text-slate-600 dark:text-slate-400">Select a pipeline instance from the library to inspect execution graph</p>
                        </div>
                    ) : (
                        <>
                            {/* Visualizer Floating Stats Bar */}
                            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-white/95 dark:bg-slate-900/90 backdrop-blur-md px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-800 shadow-md text-xs font-mono text-slate-700 dark:text-slate-300">
                                <span className="font-bold text-slate-900 dark:text-white">{selected.name || 'Active Pipeline'}</span>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span className="text-indigo-600 dark:text-indigo-400">{nodes.length} Nodes</span>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span>{edges.length} Edges</span>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span className="text-emerald-600 dark:text-emerald-400">{fmtDur(selected?.performance?.execution_time)}</span>
                            </div>

                            {/* Layout Mode Selector HUD */}
                            <div className="absolute top-4 right-4 z-10 flex items-center gap-1 bg-white/95 dark:bg-slate-900/90 backdrop-blur-md p-1 rounded-xl border border-slate-200 dark:border-slate-800 shadow-md">
                                <button
                                    onClick={() => handleLayoutChange('compact')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-mono transition-colors ${
                                        layoutMode === 'compact' 
                                            ? 'bg-indigo-600 text-white font-semibold shadow-sm' 
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                                    title="Multi-row wrap grid: highly readable global view without zooming out"
                                >
                                    <LayoutGrid size={13} />
                                    <span>Grid (Readable)</span>
                                </button>
                                <button
                                    onClick={() => handleLayoutChange('vertical')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-mono transition-colors ${
                                        layoutMode === 'vertical' 
                                            ? 'bg-indigo-600 text-white font-semibold shadow-sm' 
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                                    title="Vertical Dagre Top-to-Bottom flow"
                                >
                                    <ArrowDownCircle size={13} />
                                    <span>Vertical</span>
                                </button>
                                <button
                                    onClick={() => handleLayoutChange('horizontal')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-mono transition-colors ${
                                        layoutMode === 'horizontal' 
                                            ? 'bg-indigo-600 text-white font-semibold shadow-sm' 
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                                    title="Horizontal Left-to-Right flow"
                                >
                                    <ArrowRightCircle size={13} />
                                    <span>Horizontal</span>
                                </button>
                            </div>

                            {/* ReactFlow Canvas */}
                            <div className="flex-1 h-full w-full">
                                <ReactFlow 
                                    key={`rf-${layoutMode}-${selectedKey}`}
                                    nodes={nodes} 
                                    edges={edges} 
                                    onNodesChange={onNodesChange}
                                    onEdgesChange={onEdgesChange} 
                                    nodeTypes={nodeTypes} 
                                    fitView
                                    fitViewOptions={{ padding: 0.15, minZoom: 0.7, maxZoom: 1.2 }}
                                    className="h-full bg-slate-100/60 dark:bg-slate-950"
                                >
                                    <Background color={isDark ? "#1e293b" : "#cbd5e1"} gap={20} size={1} />
                                    <Controls className="!bg-white dark:!bg-slate-900 !border-slate-200 dark:!border-slate-800 !text-slate-700 dark:!text-white [&>button]:!bg-white dark:[&>button]:!bg-slate-900 [&>button]:!border-slate-200 dark:[&>button]:!border-slate-800 [&>button]:!text-slate-600 dark:[&>button]:!text-slate-300 hover:[&>button]:!text-slate-900 dark:hover:[&>button]:!text-white" />
                                    <MiniMap 
                                        className="!bg-white dark:!bg-slate-950 !border-slate-200 dark:!border-slate-800 rounded-xl overflow-hidden shadow-lg"
                                        nodeColor={(node) => {
                                            if (node.data?.status === 'success') return '#10b981';
                                            if (node.data?.status === 'running') return '#f59e0b';
                                            if (node.data?.status === 'fail') return '#ef4444';
                                            return isDark ? '#64748b' : '#94a3b8';
                                        }}
                                        maskColor={isDark ? "rgba(2, 6, 23, 0.7)" : "rgba(241, 245, 249, 0.7)"}
                                    />
                                </ReactFlow>
                            </div>
                        </>
                    )}

                    {/* Node Inspector Drawer */}
                    {selectedNode && (
                        <div className="absolute right-0 top-0 bottom-0 w-[540px] max-w-full bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-l border-slate-200 dark:border-slate-800 flex flex-col shadow-2xl z-30 animate-in slide-in-from-right duration-300">
                            {/* Drawer Header */}
                            <div className="p-4 bg-slate-50 dark:bg-slate-900/90 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-shrink-0">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-mono truncate">
                                            {selectedNode.step?.job || selectedNode.step?.name || selectedNode.id}
                                        </h3>
                                        {(selectedNode.step?.is_tool || selectedNode.id?.startsWith('tool_')) && (
                                            <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30 flex items-center gap-1">
                                                <Wrench size={10} /> AI Tool
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5 font-mono">
                                        {selectedNode.step?.description || 'Executed step within DAG cluster'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedNodeId(null)}
                                    className="p-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Tab Bar */}
                            <div className="px-4 pt-2 bg-slate-100/70 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 flex gap-1 flex-shrink-0">
                                {['overview', 'inputs', 'output', 'logs'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setInspectorTab(tab)}
                                        className={`px-3 py-1.5 rounded-t-lg text-xs font-mono font-semibold uppercase tracking-wider transition-all ${
                                            inspectorTab === tab
                                                ? 'bg-white dark:bg-slate-950 text-indigo-600 dark:text-indigo-400 border-t-2 border-indigo-500 border-x border-slate-200 dark:border-slate-800'
                                                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        }`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>

                            {/* Drawer Content Body */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4 font-mono text-xs scrollbar-thin">
                                {inspectorTab === 'overview' && (
                                    <>
                                        {/* Status & Latency Card */}
                                        <div className={`p-4 rounded-xl border ${
                                            selectedNode.status === 'success'
                                                ? 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950/20 dark:border-emerald-500/30 dark:text-emerald-300'
                                                : selectedNode.status === 'running'
                                                ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950/20 dark:border-amber-500/30 dark:text-amber-300'
                                                : selectedNode.status === 'fail'
                                                ? 'bg-red-50 border-red-300 text-red-800 dark:bg-red-950/20 dark:border-red-500/30 dark:text-red-300'
                                                : 'bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-900/60 dark:border-slate-800 dark:text-slate-300'
                                        }`}>
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                                                    {selectedNode.status === 'success' && <Check size={14} />}
                                                    {selectedNode.status === 'running' && <Loader2 size={14} className="animate-spin" />}
                                                    {selectedNode.status === 'fail' && <AlertCircle size={14} />}
                                                    Status: {selectedNode.status || 'pending'}
                                                </span>
                                                <span className="text-[11px] opacity-75 font-mono">ID: {selectedNode.id}</span>
                                            </div>

                                            {selectedNode.performance && (
                                                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200 dark:border-white/10 text-xs">
                                                    <div>
                                                        <div className="text-slate-500 dark:text-slate-400 text-[10px] uppercase">Latency</div>
                                                        <div className="font-bold text-slate-900 dark:text-white mt-0.5">{fmtDur(selectedNode.performance.execution_time)}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-slate-500 dark:text-slate-400 text-[10px] uppercase">Memory</div>
                                                        <div className="font-bold text-slate-900 dark:text-white mt-0.5">{(selectedNode.performance.memory_used / 1024 / 1024).toFixed(2)} MB</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-slate-500 dark:text-slate-400 text-[10px] uppercase">Peak RAM</div>
                                                        <div className="font-bold text-slate-900 dark:text-white mt-0.5">{(selectedNode.performance.peak_memory / 1024 / 1024).toFixed(2)} MB</div>
                                                    </div>
                                                </div>
                                            )}

                                            {selectedNode?.error?.length > 0 && (
                                                <div className="mt-3 p-3 rounded-lg bg-red-100 border border-red-300 text-red-800 dark:bg-red-950/60 dark:border-red-500/40 dark:text-red-200 text-xs space-y-1">
                                                    <div className="font-bold flex items-center gap-1.5 text-red-700 dark:text-red-300">
                                                        <ShieldAlert size={14} /> Execution Exceptions:
                                                    </div>
                                                    {selectedNode.error.map((err, i) => (
                                                        <div key={i} className="pl-5 text-red-800 dark:text-red-300/90">{err}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Runtime Metadata */}
                                        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-3">
                                            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                Runtime Telemetry
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3 text-xs">
                                                <div>
                                                    <span className="text-slate-400 dark:text-slate-500 block text-[10px]">EXECUTOR</span>
                                                    <span className="text-slate-800 dark:text-slate-200 font-semibold">{selectedNode.executor || 'Standard Worker'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 dark:text-slate-500 block text-[10px]">TOTAL TOKENS</span>
                                                    <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{selectedNode.total_tokens || '0'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 dark:text-slate-500 block text-[10px]">START TIME</span>
                                                    <span className="text-slate-700 dark:text-slate-300">{fmtDate(selectedNode.performance?.start_time)}</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 dark:text-slate-500 block text-[10px]">END TIME</span>
                                                    <span className="text-slate-700 dark:text-slate-300">{fmtDate(selectedNode.performance?.end_time)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Globals */}
                                        {selected?.globals && (
                                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-2">
                                                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                    Global Environment State
                                                </h4>
                                                <div className="p-2.5 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 max-h-48 overflow-y-auto">
                                                    <JsonView 
                                                        value={selected.globals} 
                                                        collapsed={1} 
                                                        style={{
                                                            ...(isDark ? darkTheme : lightTheme),
                                                            backgroundColor: 'transparent',
                                                            fontSize: '11px'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {inspectorTab === 'inputs' && (
                                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-2">
                                        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center justify-between">
                                            <span>Step Input Parameters</span>
                                            <Code size={13} className="text-indigo-600 dark:text-indigo-400" />
                                        </h4>
                                        <div className="p-3 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 max-h-[550px] overflow-y-auto">
                                            <JsonView 
                                                value={selectedNode.step?.inputs || {}} 
                                                collapsed={false}
                                                style={{
                                                    ...(isDark ? darkTheme : lightTheme),
                                                    backgroundColor: 'transparent',
                                                    fontSize: '11px'
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {inspectorTab === 'output' && (
                                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-2">
                                        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center justify-between">
                                            <span>Execution Result Payload</span>
                                            <Sparkles size={13} className="text-emerald-600 dark:text-emerald-400" />
                                        </h4>
                                        <div className="p-3 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 max-h-[550px] overflow-y-auto">
                                            <JsonView 
                                                value={selectedNode.output || {}} 
                                                collapsed={false}
                                                style={{
                                                    ...(isDark ? darkTheme : lightTheme),
                                                    backgroundColor: 'transparent',
                                                    fontSize: '11px'
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {inspectorTab === 'logs' && (
                                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-2">
                                        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center justify-between">
                                            <span>Step Execution Stream</span>
                                            <Terminal size={13} className="text-amber-600 dark:text-amber-400" />
                                        </h4>
                                        {selectedNode.logs?.length > 0 ? (
                                            <div className="p-3 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 max-h-[550px] overflow-y-auto space-y-1.5">
                                                {selectedNode.logs.map((log, idx) => (
                                                    <div key={idx} className="flex items-start gap-2 text-emerald-700 dark:text-emerald-400">
                                                        <span className="text-slate-400 dark:text-slate-600 select-none text-[10px]">{idx + 1}</span>
                                                        <span className="break-all">{log}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="py-12 text-center text-slate-400 dark:text-slate-500 font-mono">
                                                No log stream captured for this execution node.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
